<template>
  <span class="cinfo">
    <span class="cinfo__btn" role="button" tabindex="0" aria-label="Cómo se calcula" title="Cómo se calcula" @click.stop="open" @keydown.enter.stop.prevent="open">
      <span class="mdi mdi-information-outline"></span>
    </span>

    <Teleport to="body">
      <div v-if="visible" class="cinfo-overlay" @click.self="visible = false">
        <div class="cinfo-modal" role="dialog" aria-modal="true">
          <div class="cinfo-modal__header">
            <span class="cinfo-modal__title">{{ title }}</span>
            <button class="cinfo-modal__close" @click="visible = false"><span class="mdi mdi-close"></span></button>
          </div>

          <div class="cinfo-modal__body">
            <p v-for="(t, i) in paragraphs" :key="i" class="cinfo-modal__text">{{ t }}</p>

            <div v-if="loading" class="cinfo-modal__state">Cargando desglose...</div>
            <div v-else-if="error" class="cinfo-modal__state cinfo-modal__state--error">{{ error }}</div>
            <template v-else-if="data">
              <span class="cinfo-modal__section">Cargos que se suman ({{ data.rows.length }})</span>
              <table v-if="data.rows.length" class="cinfo-modal__table">
                <thead><tr><th>Inquilino</th><th>Cargo</th><th>Vence</th><th class="r">Cobrado</th></tr></thead>
                <tbody>
                  <tr v-for="r in data.rows" :key="r.chargeId">
                    <td>{{ r.tenantName }}<span class="cinfo-modal__sub">{{ r.plotName }}</span></td>
                    <td>{{ r.concept }}</td>
                    <td>{{ formatDate(r.dueDate) }}</td>
                    <td class="r">{{ formatCurrency(r.counted) }}</td>
                  </tr>
                </tbody>
                <tfoot><tr><td colspan="3">Total cobrado</td><td class="r">{{ formatCurrency(data.total) }}</td></tr></tfoot>
              </table>
              <p v-else class="cinfo-modal__empty">Todavía no hay pagos aplicados a cargos de este mes.</p>

              <template v-if="data.outside.rows.length">
                <span class="cinfo-modal__section cinfo-modal__section--muted">No se suman aquí ({{ formatCurrency(data.outside.total) }})</span>
                <p class="cinfo-modal__text cinfo-modal__text--muted">{{ outsideNote }}</p>
                <table class="cinfo-modal__table cinfo-modal__table--muted">
                  <tbody>
                    <tr v-for="r in data.outside.rows" :key="r.chargeId">
                      <td>{{ r.tenantName }}<span class="cinfo-modal__sub">{{ r.plotName }}</span></td>
                      <td>{{ r.concept }}</td>
                      <td>{{ formatDate(r.dueDate) }}</td>
                      <td class="r">{{ formatCurrency(r.counted) }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>

              <template v-if="data.cash">
                <span class="cinfo-modal__section">Efectivo recibido en el mes ({{ formatCurrency(data.cash.total) }})</span>
                <p class="cinfo-modal__text cinfo-modal__text--muted">
                  Todos los pagos con fecha de este mes, sin importar a qué cargo o periodo se aplicaron. Es la cifra que debe cuadrar con el banco.
                  <template v-if="data.cash.unappliedTotal > 0"> Incluye {{ formatCurrency(data.cash.unappliedTotal) }} de saldo a favor o adelantos todavía sin cargo.</template>
                </p>
                <table v-if="data.cash.rows.length" class="cinfo-modal__table">
                  <thead><tr><th>Inquilino</th><th>Fecha</th><th>Se aplicó a</th><th class="r">Pago</th></tr></thead>
                  <tbody>
                    <tr v-for="r in data.cash.rows" :key="r.paymentId">
                      <td>{{ r.tenantName }}<span class="cinfo-modal__sub">{{ r.plotName }}</span></td>
                      <td>{{ formatDate(r.date) }}</td>
                      <td>
                        <span v-for="(a, i) in r.appliedTo" :key="i" class="cinfo-modal__apply">{{ a.concept }} · {{ periodText(a) }} ({{ formatCurrency(a.amount) }})</span>
                        <span v-if="r.unapplied > 0" class="cinfo-modal__apply cinfo-modal__apply--credit">Saldo a favor ({{ formatCurrency(r.unapplied) }})</span>
                      </td>
                      <td class="r">{{ formatCurrency(r.amount) }}</td>
                    </tr>
                  </tbody>
                  <tfoot><tr><td colspan="3">Efectivo recibido</td><td class="r">{{ formatCurrency(data.cash.total) }}</td></tr></tfoot>
                </table>
                <p v-else class="cinfo-modal__empty">No se han registrado pagos en este mes.</p>
              </template>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getCollectedBreakdown } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, default: '' },
  period: { type: String, default: '' }, // YYYY-MM
  basis: { type: String, required: true }, // 'period' (Cobranza) | 'due' (Finanzas)
  title: { type: String, required: true },
  paragraphs: { type: Array, default: () => [] },
})

const visible = ref(false)
const loading = ref(false)
const error = ref('')
const data = ref(null)

const outsideNote = computed(() =>
  'Son pagos de cargos que vencen este mes pero pertenecen a otro periodo. Se cuentan en el periodo del cargo, no aquí.'
)

async function open() {
  visible.value = true
  if (!props.parkId || !props.period) return
  loading.value = true
  error.value = ''
  try {
    data.value = await getCollectedBreakdown(props.parkId, props.period, props.basis)
  } catch (err) {
    error.value = err.message
    data.value = null
  } finally {
    loading.value = false
  }
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 }).format(n || 0)
}
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
function periodText(a) { return `${MONTHS_SHORT[a.periodMonth - 1]} ${a.periodYear}` }
function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
</script>

<style scoped>
.cinfo { display: inline-flex; vertical-align: middle; }
.cinfo__btn { display: inline-flex; align-items: center; justify-content: center; margin-left: 0.3rem; color: #94a3b8; cursor: pointer; font-size: 14px; line-height: 1; border-radius: 50%; }
.cinfo__btn:hover, .cinfo__btn:focus-visible { color: #3b82f6; outline: none; }
.cinfo-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 1rem; }
.cinfo-modal { width: 640px; max-width: 100%; max-height: 85vh; background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column; overflow: hidden; text-align: left; }
.cinfo-modal__header { flex-shrink: 0; background: #0f172a; display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.25rem; }
.cinfo-modal__title { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #fff; }
.cinfo-modal__close { background: none; border: none; color: rgba(255, 255, 255, 0.6); cursor: pointer; display: flex; }
.cinfo-modal__body { padding: 1.1rem 1.25rem 1.25rem; overflow-y: auto; font-family: var(--font-body); color: #334155; scrollbar-width: thin; scrollbar-color: var(--border-active) transparent; }
.cinfo-modal__body::-webkit-scrollbar { width: 6px; }
.cinfo-modal__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cinfo-modal__text { margin: 0 0 0.6rem; font-size: 13px; line-height: 1.5; }
.cinfo-modal__text--muted { color: #94a3b8; font-size: 12px; }
.cinfo-modal__state { margin-top: 0.8rem; font-size: 13px; color: #94a3b8; }
.cinfo-modal__state--error { color: #dc2626; }
.cinfo-modal__section { display: block; margin: 1rem 0 0.4rem; font-size: 12px; font-weight: 500; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.cinfo-modal__section--muted { color: #94a3b8; }
.cinfo-modal__table { width: 100%; border-collapse: collapse; font-size: 12px; }
.cinfo-modal__table th { text-align: left; font-size: 10px; text-transform: uppercase; color: #94a3b8; padding-bottom: 0.3rem; border-bottom: 1px solid #e2e8f0; }
.cinfo-modal__table td { padding: 0.4rem 0.3rem 0.4rem 0; border-bottom: 1px solid #f1f5f9; color: #1e3a5f; vertical-align: top; }
.cinfo-modal__table .r { text-align: right; font-family: var(--font-mono); white-space: nowrap; }
.cinfo-modal__table tfoot td { font-weight: 700; background: #f8f9fc; padding-left: 0.3rem; }
.cinfo-modal__table--muted td { color: #94a3b8; }
.cinfo-modal__sub { display: block; font-size: 10px; color: #94a3b8; }
.cinfo-modal__apply { display: block; font-size: 11px; color: #64748b; }
.cinfo-modal__apply--credit { color: #d97706; }
.cinfo-modal__empty { font-size: 13px; color: #94a3b8; }
</style>